import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import {
  useLoginSuperAdminMutation,
  useLoginBusinessAdminMutation,
} from "../../store/api";
import { setCredentials } from "../../store/authSlice";
import { clearLicenseExpired, setLicenseExpired } from "../../store/licenseSlice";
import { isLicenseExpiredProblem } from "../../utils/problemDetails";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuperAdmin, superAdminState] = useLoginSuperAdminMutation();
  const [loginBusinessAdmin, businessAdminState] =
    useLoginBusinessAdminMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginError, setLoginError] = useState(null);
  const isLoading = superAdminState.isLoading || businessAdminState.isLoading;

  const EyeIcon = ({ hidden }) => (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {hidden ? (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a2.5 2.5 0 103.54 3.54" />
          <path d="M9.88 5.09A10.94 10.94 0 0112 5c4.5 0 8.31 2.78 10 7-1.2 3.01-3.28 5.28-5.84 6.56" />
          <path d="M6.08 6.08C3.9 7.46 2.23 9.41 1 12c1.69 4.22 5.5 7 10 7 .73 0 1.45-.07 2.14-.21" />
        </>
      ) : (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoginError(null);

    try {
      // Business-scoped screens need the business-admin token because it carries
      // the business claim used by /admin/business/* endpoints.
      const result = await loginBusinessAdmin(form).unwrap();
      const token = result?.accessToken || result?.token;
      const user = result?.user || result?.profile || null;
      dispatch(setCredentials({ token, user }));
      dispatch(clearLicenseExpired());

      // Redirect based on user role
      if (user?.isPlatformAdmin === true) {
        navigate("/businesses");
      } else {
        navigate("/menu/categories");
      }
    } catch (businessAdminError) {
      if (isLicenseExpiredProblem(businessAdminError)) {
        dispatch(
          setLicenseExpired({
            code: "LICENSE_EXPIRED",
            message:
              businessAdminError?.data?.detail ||
              "License expired. Please renew to continue.",
          }),
        );
        navigate("/license-expired", { replace: true });
        return;
      }

      // If business admin login fails, try super admin login.
      try {
        const result = await loginSuperAdmin(form).unwrap();
        const token = result?.accessToken || result?.token;
        const user = result?.user || result?.profile || null;
        dispatch(setCredentials({ token, user }));
        dispatch(clearLicenseExpired());

        // Redirect based on user role
        if (user?.isPlatformAdmin === true) {
          navigate("/businesses");
        } else {
          navigate("/menu/categories");
        }
      } catch (superAdminError) {
        // Both failed, show error
        setLoginError(
          businessAdminError?.data?.detail ||
            superAdminError?.data?.detail ||
            "Invalid email or password. Please try again.",
        );
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] px-4 py-12">
      <div className="mx-auto w-full max-w-xl sm:p-10">
        <Card>
          <h1 className="text-center text-2xl font-semibold text-slate-900">
            Admin Portal
          </h1>
          <p className="mt-2 text-center text-sm text-slate-600">
            Sign in to manage your restaurant operations
          </p>

          <form className="mt-8 flex flex-col gap-5" onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              name="email"
              placeholder="admin@example.com"
              value={form.email}
              onChange={handleChange}
              required
            />

            <Input
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              value={form.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              rightElement={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  <EyeIcon hidden={showPassword} />
                </button>
              }
            />

            {loginError && <Alert tone="error">{loginError}</Alert>}

            <Button type="submit" className="mt-2 w-full" disabled={isLoading}>
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
